import { useMemo, useContext } from "react";
import { DeliveryState, Workflow } from "../../../enums/orders";
import { OrderRoute, OrderRouteLeg, OrderRouteLegStatus } from "../../../interfaces/common/routes";
import { OrderType } from "../../../interfaces/delivery-company/orders";
import LebanonMapFlow, { LocationsLink, LocationType, MapLocationItem, LinkStatus } from "../lebanon-map/lebanon-map";
import { MapboxStyle } from "../lebanon-map/lebanon-map-base-mapbox";
import { DomainForcedOrganisationContext } from "../../../contexts/domain-forced-organisation-context";

interface RouteMapProps { 
    order: OrderType;
    orderRoute?: OrderRoute;
    width?: number;
    height?: number;
    scale?: number;
    zoom?: number;
    useStaticMap?: boolean;
    mapStyle?: MapboxStyle;
}

const getEdgeColorForLeg = (leg: OrderRouteLeg) => {
    if (leg.status == OrderRouteLegStatus.FINISHED) return "#2e7d32" // Material green
    if (leg.status == OrderRouteLegStatus.STARTED) return "#ed6c02" // Material orange
    return "#757575" // Material grey
}

const getEdgeColorForOrder = (order: OrderType) => {
    if ([
            DeliveryState.CREATED, 
            DeliveryState.PICKUP_REQUESTED, 
            DeliveryState.PICKUP_ASSIGNED,
        ].includes(order.delivery_state)
    ) return "#757575"

    if (order.delivery_state == DeliveryState.PICKED_UP) return "#ed6c02"

    return "#2e7d32"
}

const getStatusForLeg = (leg: OrderRouteLeg): LinkStatus => {
    if (leg.status === OrderRouteLegStatus.FINISHED) return "completed";
    if (leg.status === OrderRouteLegStatus.STARTED) return "in_progress";
    return "pending";
}

const getStatusForPickup = (order: OrderType): LinkStatus => {
    if ([
        DeliveryState.PICKED_UP,
        DeliveryState.DELIVERED,
        DeliveryState.AT_WAREHOUSE,
        DeliveryState.DELIVERY_ASSIGNED,
        DeliveryState.PICKED_UP_FOR_DELIVERY,
        DeliveryState.IN_TRANSIT
    ].includes(order.delivery_state)) {
        return "completed";
    }
    if ([DeliveryState.PICKUP_ASSIGNED].includes(order.delivery_state)) {
        return "in_progress";
    }
    return "pending";
}

const getStatusForLastMile = (order: OrderType): LinkStatus => {
    if (order.delivery_state === DeliveryState.DELIVERED) {
        return "completed";
    }
    if ([
        DeliveryState.DELIVERY_ASSIGNED,
        DeliveryState.PICKED_UP_FOR_DELIVERY,
        DeliveryState.IN_TRANSIT
    ].includes(order.delivery_state)) {
        return "in_progress";
    }
    return "pending";
}

export const RouteMap = ({orderRoute, order, width = 600, height = 550, zoom=7.5, scale=14000, useStaticMap = false, mapStyle = 'streets-v12'}: RouteMapProps) => {

    const { forcedOrganisation } = useContext(DomainForcedOrganisationContext);

    const [locations, links] = useMemo(()=>{

        const locations: MapLocationItem[] = []
        const links: LocationsLink[] = []

        // calculating locations 
        // Get actual names
        const merchantName = order.merchant?.name || "Merchant";
        const customerName = order.customer?.name || "Customer";
        
        // Add source location (pickup point)
        if (order.workflow == Workflow.STANDARD) {
            locations.push({
                id: "source",
                label: merchantName,
                area: order.merchant_location?.area,
                type: LocationType.SHOP,
                logoUrl: order.merchant?.logo_url,
            });
        } else {
            locations.push({
                id: "source",
                label: customerName,
                area: order.customer_location?.area,
                type: LocationType.HOME,
            });
        }

        // Add warehouse branches if route exists
        if (orderRoute) {
            orderRoute.branches.forEach(b => locations.push({
                "id": b.id,
                label: b.name,
                area: b.area,
                type: LocationType.WAREHOUSE,
                logoUrl: forcedOrganisation?.logoUrl,
            }));
        }
            
        // Add target location (delivery point) - always add this
        if (order.workflow == Workflow.RETURN) {
            locations.push({
                    id: "target",
                label: merchantName,
                    area: order.merchant_location?.area,
                    type: LocationType.SHOP,
                logoUrl: order.merchant?.logo_url,
            });
        } else {
            locations.push({
                    id: "target",
                label: customerName,
                    area: order.customer_location?.area,
                    type: LocationType.HOME,
            });
        }

        // calculating links
        links.push({
            locationA: "source",
            locationB: orderRoute ? orderRoute.branches[0].id : "target",
            label: orderRoute ? "Pickup" : "Direct",
            type: "straight",
            directed: true,
            color: getEdgeColorForOrder(order),
            status: getStatusForPickup(order),
        })

        if (orderRoute) {
            orderRoute.legs.forEach(leg => links.push({
                locationA: leg.source.id,
                locationB: leg.destination.id,
                label: "Transfer",
                type: "straight",
                directed: true,
                color: getEdgeColorForLeg(leg),
                status: getStatusForLeg(leg),
            }))

            links.push({
                locationA: orderRoute.branches[orderRoute.branches.length-1].id,
                locationB: "target",
                label: "Last Mile",
                type: "straight",
                directed: true,
                color: order.delivery_state == DeliveryState.DELIVERED ? "#2e7d32" : "#757575",
                status: getStatusForLastMile(order),
            })
        } 

        return [locations, links]

    }, [order, orderRoute, forcedOrganisation?.logoUrl]);
    
    return <LebanonMapFlow 
            showDistricts={false}
            showPartitions={false}
            showBorder={false}
            locations={locations}
            links={links} 
            scale={scale}
            loading={!orderRoute}
            width={width}
            height={height}
            zoom={zoom}
            useStaticMap={useStaticMap}
            useNativeRoutes={!useStaticMap}
            interactive={!useStaticMap}
            mapStyle={mapStyle}
        />
}